<template>
  <MediumSizedModal :show="showModal" title="Notification Info" @close="toggleModal">
    <div
      v-if="
        selectedNotification &&
        selectedNotification.notif_source_type === 'BRGYCLRNCAPPREQ'
      "
    >
      <SpinnerBlock v-if="isGettingNotifInfo" />
      <!-- <ClearanceInfo :app-data="loadedClearance" @close="toggleModal" /> -->
    </div>
  </MediumSizedModal>
  <div class="notification-menu" ref="menuContainer">
    <!-- Custom Notification Dropdown -->
    <div class="notification-trigger" @click="toggleNotifications" ref="notificationBtn">
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z"
          :stroke="showNotifications ? '#4f46e5' : '#6b7280'"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21"
          :stroke="showNotifications ? '#4f46e5' : '#6b7280'"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>

      <span v-if="unreadCount > 0" class="notification-badge">
        {{ unreadCount > 99 ? "99+" : unreadCount }}
      </span>
    </div>

    <!-- Notification Dropdown -->
    <transition name="notification-menu">
      <div
        v-show="showNotifications"
        class="notification-panel"
        :class="{ 'panel-open': showNotifications }"
      >
        <!-- Header -->
        <div class="notification-header">
          <h3>Notifications</h3>
          <span v-if="unreadCount > 0" class="unread-count">
            {{ unreadCount }} unread
          </span>
        </div>

        <!-- Notifications List -->
        <div class="notification-list">
          <div
            v-for="notification in notifications"
            :key="notification.id"
            class="notification-item"
            :class="{ unread: !notification.isRead }"
            @click="markAsRead(notification.id)"
          >
            <div
              class="notification-icon-wrapper"
              :class="getStateByType(notification.type)"
            >
              <i :class="getIconByType(notification.type)"></i>
            </div>
            <div class="notification-content">
              <p :class="['notification-message', { 'fw-bold': !notification.isRead }]">
                {{ notification.header }}
              </p>
              <span class="notification-time"
                >{{ notification.timeAgo }}
                <span v-if="notification.timeAgo !== 'just now'"> ago</span>
              </span>
            </div>
            <div v-if="!notification.isRead" class="unread-indicator"></div>
          </div>

          <div v-if="notifications.length === 0" class="empty-state">
            <i class="empty-icon">🔔</i>
            <p>No notifications yet</p>
            <small>We'll notify you when something arrives</small>
          </div>
        </div>

        <!-- Footer -->
        <div class="notification-footer">
          <router-link
            to="/notifications/all"
            class="view-all-btn"
            @click="viewAllNotifications"
          >
            View All Notifications
          </router-link>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import notificationService from "@/services/notificationService";
import { ref, computed, onMounted, onUnmounted } from "vue";
import signalRService from "@/services/signalRService";
import SpinnerBlock from "../animations/SpinnerBlock.vue";
import MediumSizedModal from "../modals/MediumSizedModal.vue";
import { useHeaderEvents } from "@/composables/userHeadEvents";

// Reactive state
const showModal = ref(false);
const showNotifications = ref(false);
const isGettingNotifInfo = ref(false);
const notifications = ref([]);
const menuContainer = ref(null);

const toggleModal = () => {
  showModal.value = !showModal.value;
};

const getIconByType = (type) => {
  switch (type) {
    default:
      return "bi bi-chat";
  }
};

const getStateByType = (type) => {
  switch (type) {
    default:
      return "info";
  }
};

// Refs for DOM elements
const notificationBtn = ref(null);
const selectedNotification = ref(null);

// Computed properties
const unreadCount = computed(() => {
  return notifications.value.filter((n) => !n.isRead).length;
});

// Methods
const toggleNotifications = () => {
  showNotifications.value = !showNotifications.value;
};

const closeNotifications = () => {
  showNotifications.value = false;
};

const markAsRead = (id) => {
  const notification = notifications.value.find((n) => n.id === id);
  if (notification && !notification.isRead) {
    notification.isRead = true;
  }
  selectedNotification.value = notification;
  closeNotifications();
  getClearanceById(notification.id, notification.id);
  toggleModal();
};

const getClearanceById = async (id, notifId) => {
  id;
  notifId;
  isGettingNotifInfo.value = true;
  // loadedClearance.value = (await clearanceService.getClearanceById(id, notifId)).data;
  isGettingNotifInfo.value = false;
};

const viewAllNotifications = () => {
  console.log("View all notifications clicked");
  closeNotifications();
};

// Click outside to close
const handleClickOutside = (event) => {
  if (menuContainer.value && !menuContainer.value.contains(event.target)) {
    closeNotifications();
  }
};

// Keyboard navigation
const handleKeydown = (event) => {
  if (!showNotifications.value) return;

  if (event.key === "Escape") {
    closeNotifications();
  }
};

const getNotifications = async () => {
  notifications.value = await notificationService.getNotifications();
};

const { emit } = useHeaderEvents();

const broadcastEvent = () => {
  emit({
    type: "notification-received",
    message: "New notifaction received",
    data: { action: "from-signalr", timestamp: new Date() },
  });
};

const subscribeToNewNotifications = () => {
  if (signalRService.connection)
    signalRService.connection.on("NewNotification", (item) => {
      item;
      broadcastEvent();
      getNotifications();
    });
  else {
    setTimeout(() => {
      subscribeToNewNotifications();
    }, 100);
  }
};

// Lifecycle hooks
onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  document.addEventListener("keydown", handleKeydown);
  getNotifications();
  subscribeToNewNotifications();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("keydown", handleKeydown);

  if (signalRService.connection)
    signalRService.connection.off("NewNotification", () => {
      //
    });
});
</script>

<style scoped>
.notification-menu {
  position: relative;
  display: inline-block;
  margin-right: 15px;
}

/* Notification Trigger */
.notification-trigger {
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-color: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid transparent;
}

.notification-trigger:hover {
  background-color: #e2e8f0;
  transform: translateY(-1px);
}

.notification-trigger.active {
  background-color: #e54646;
  border-color: #e54646;
}

.notification-trigger.active svg path {
  stroke: white;
}

.notification-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  background: #ef4444;
  color: white;
  border-radius: 10px;
  min-width: 18px;
  height: 18px;
  font-size: 0.7rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  border: 2px solid white;
}

/* Notification Panel */
.notification-panel {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.75rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 107, 107, 0.1);
  width: 360px;
  z-index: 1100;
  overflow: hidden;
  transform-origin: top right;
}

/* Menu Animation */
.notification-menu-enter-active,
.notification-menu-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.notification-menu-enter-from {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}

.notification-menu-enter-to {
  opacity: 1;
  transform: scale(1) translateY(0);
}

.notification-menu-leave-from {
  opacity: 1;
  transform: scale(1) translateY(0);
}

.notification-menu-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}

.notification-header {
  padding: 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #f5fff8 0%, #e6ffee 100%);
}

.notification-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
}

.unread-count {
  background: #4f46e5;
  color: white;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
}

.notification-list {
  max-height: 320px;
  overflow-y: auto;
}

.notification-item {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  position: relative;
}

.notification-item:hover {
  background-color: #f8fafc;
}

.notification-item.unread {
  background-color: #f0f9ff;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-icon-wrapper {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.notification-icon-wrapper.info {
  background-color: #dbeafe;
  color: #2563eb;
}

.notification-icon-wrapper.success {
  background-color: #dcfce7;
  color: #16a34a;
}

.notification-icon-wrapper.warning {
  background-color: #fef3c7;
  color: #d97706;
}

.notification-icon-wrapper.danger {
  background-color: #fee2e2;
  color: #dc2626;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-message {
  margin: 0 0 4px 0;
  color: #374151;
  font-size: 0.9rem;
  line-height: 1.4;
}

.notification-time {
  color: #6b7280;
  font-size: 0.8rem;
}

.unread-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4f46e5;
  flex-shrink: 0;
  margin-top: 4px;
}

.empty-state {
  padding: 40px 20px;
  text-align: center;
  color: #6b7280;
}

.empty-icon {
  font-size: 2rem;
  margin-bottom: 12px;
  display: block;
}

.empty-state p {
  margin: 0 0 4px 0;
  font-weight: 500;
}

.empty-state small {
  font-size: 0.85rem;
}

.notification-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #f1f5f9;
}

.view-all-btn {
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s ease;
  text-align: center;
  display: block;
}

.view-all-btn:hover {
  background: #f8fafc;
  border-color: #9ca3af;
}

/* Scrollbar styling */
.notification-list::-webkit-scrollbar {
  width: 6px;
}

.notification-list::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.notification-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.notification-list::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Responsive Design - Same as user profile menu */
@media (max-width: 768px) {
  .notification-menu {
    margin-right: 8px;
  }

  .notification-trigger {
    width: 40px;
    height: 40px;
  }

  .notification-badge {
    min-width: 16px;
    height: 16px;
    font-size: 0.65rem;
  }

  .notification-panel {
    position: fixed;
    top: 70px;
    left: 1rem;
    right: 1rem;
    width: auto;
    border-radius: 16px;
    z-index: 9999;
  }
}

@media (max-width: 480px) {
  .notification-panel {
    left: 0.5rem;
    right: 0.5rem;
  }

  .notification-item {
    padding: 0.875rem 1.25rem;
  }

  .notification-header {
    padding: 1.25rem;
  }

  .notification-footer {
    padding: 0.875rem 1.25rem;
  }
}

/* Focus styles for accessibility */
.notification-trigger:focus-visible {
  outline: 2px solid #ff6b6b;
  outline-offset: 2px;
}

/* Ensure high z-index to appear above Bootstrap elements */
.notification-menu {
  z-index: 1100;
}

.notification-panel {
  z-index: 1101;
}
</style>
